import { Badge } from "@shadcn/ui/badge";
import { cn } from "@shadcn/lib/utils";
import { Link } from "@tanstack/react-router";
import { ChevronRight, type LucideIcon } from "lucide-react";

type SupportLinkRowProps = {
	icon: LucideIcon;
	title: React.ReactNode;
	badgeLabel?: React.ReactNode;
	to?: string;
	onClick?: () => void;
	linkProps?: React.AnchorHTMLAttributes<HTMLAnchorElement>;
	className?: string;
};

const content = (props: Omit<SupportLinkRowProps, "to" | "onClick" | "linkProps" | "className">) => {
	const { icon: Icon, title, badgeLabel } = props;

	return (
		<>
			<div className="flex shrink-0 items-center justify-center">
				<Icon className="size-6" />
			</div>
			<div className="flex min-w-0 flex-1 items-center gap-4">
				<p className="min-w-0 flex-1 font-breit text-base leading-none text-card-foreground">{title}</p>
				{badgeLabel ? (
					<Badge className="h-5 rounded-full border-transparent bg-primary px-1.5 font-breit text-xs leading-4 text-primary-foreground shadow-none">
						{badgeLabel}
					</Badge>
				) : null}
			</div>
			<ChevronRight className="size-5 shrink-0" />
		</>
	);
};

export const SupportLinkRow = (props: SupportLinkRowProps) => {
	const { to, onClick, linkProps, className } = props;
	const sharedClassName = cn(
		"border-border flex w-full items-center gap-4 border-t py-5 text-left transition-colors",
		(to || onClick || linkProps) && "cursor-pointer hover:bg-accent/20",
		className
	);

	if (to) {
		return (
			<Link to={to} className={sharedClassName}>
				{content(props)}
			</Link>
		);
	}

	if (onClick) {
		return (
			<button type="button" className={sharedClassName} onClick={onClick}>
				{content(props)}
			</button>
		);
	}

	if (linkProps) {
		return (
			<a className={sharedClassName} {...linkProps}>
				{content(props)}
			</a>
		);
	}

	return <div className={sharedClassName}>{content(props)}</div>;
};
